"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import { useTheme } from "next-themes";

import "./auth-tokens.css";
import { IconClose, IconMoon, IconSun } from "./AuthIcons";

const ICON_BTN: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 6,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "transparent",
  border: "1px solid transparent",
  color: "var(--auth-text-muted)",
  cursor: "pointer",
  transition: "background 0.12s, color 0.12s",
};

/**
 * Centered single-column auth layout. Renders a topbar (theme toggle + close)
 * and a small brand mark, then the screen content centered in the remaining
 * space.
 *
 * `onClose` defaults to navigating home; pass your own to override.
 * `showBrand` controls the small "WiLine Workforce" mark beneath the topbar.
 */
export function AuthShell({
  children,
  onClose,
  showBrand = true,
}: {
  readonly children: React.ReactNode;
  readonly onClose?: () => void;
  readonly showBrand?: boolean;
}) {
  const router = useRouter();
  const { resolvedTheme, setTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const handleClose = onClose ?? (() => router.replace("/"));

  return (
    <div
      data-auth-shell
      data-theme={isDark ? "dark" : "light"}
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "var(--auth-bg)",
        color: "var(--auth-text)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "20px 24px",
        }}
      >
        <div />
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <button
            type="button"
            aria-label="Toggle theme"
            onClick={() => setTheme(isDark ? "light" : "dark")}
            style={ICON_BTN}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--auth-bg-muted)";
              e.currentTarget.style.color = "var(--auth-text)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "var(--auth-text-muted)";
            }}
          >
            {isDark ? <IconSun size={16} /> : <IconMoon size={16} />}
          </button>
          <button
            type="button"
            aria-label="Close"
            onClick={handleClose}
            style={ICON_BTN}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--auth-bg-muted)";
              e.currentTarget.style.color = "var(--auth-text)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "var(--auth-text-muted)";
            }}
          >
            <IconClose size={16} />
          </button>
        </div>
      </div>

      {showBrand && (
        <div
          style={{
            padding: "0 24px",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <img
            src="/wilabs-logo.svg"
            alt=""
            width={24}
            height={24}
            style={{ borderRadius: 6, display: "block" }}
          />
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: "-0.01em",
            }}
          >
            WiLine Workforce
          </span>
        </div>
      )}

      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px 24px 80px",
        }}
      >
        {children}
      </div>
    </div>
  );
}
